import { writable, get } from 'svelte/store';

import { fetchCount, type SelectionCount } from '$lib/api/count';
import { billableRecords } from '$lib/pricing/pricing';
import { logger } from '$lib';

/**
 * State for the guided search — classic mode's front door.
 *
 * # It is a different way of filling the same stores
 *
 * Nothing here holds a filter. Every choice the guided screen collects is
 * written straight into `queryFilters` and `selectedIndustryCentroids`, which
 * is what the filter panel, the search and the price bar already read. That is
 * deliberate and it is what keeps this cheap: leaving the guided screen at any
 * step lands you in classic mode with everything chosen so far applied, so
 * "skip this" costs nothing to implement and a returning customer can bypass
 * it entirely.
 *
 * The alternative — the guided screen owning its own draft and committing it
 * at the end — needs a translation layer, and a half-finished draft that the
 * rest of the app cannot see.
 */

/**
 * Which of the four things the customer knows first.
 *
 * `list` is the odd one out: the other three ask the customer to describe who
 * they are looking for, and this one works it out from a file of who they
 * already have. It is the door for the customer who cannot answer the other
 * three — which is most of them, because "larger companies in our sector" is
 * what people say and rarely what their book shows.
 */
export type Door = 'industry' | 'role' | 'similarity' | 'list';

/** The steps, in the order they are asked. */
export type StepName =
  | 'door'
  | 'samples'
  | 'listUpload'
  | 'listProfile'
  | 'industry'
  | 'role'
  | 'region'
  | 'refine'
  | 'legalForm'
  | 'channels'
  | 'technology'
  | 'founding'
  | 'exclude';

/** Whether the guided screen is showing, as opposed to the plain filters. */
export const guidedActive = writable(true);

/** Which door was taken, or null while the question is still on screen. */
export const guidedDoor = writable<Door | null>(null);

/** Where in the sequence we are. */
export const guidedStep = writable<StepName>('door');

/** How a count request ended. */
export type CountStatus = 'idle' | 'counting' | 'ready' | 'failed';

export interface CountState {
  status: CountStatus;

  /** The last answer, kept while a new one is in flight so the bar does not flicker. */
  value: SelectionCount | null;

  /**
   * The last billable count that was not zero.
   *
   * A running total reaching zero is the moment worth designing for: the
   * customer has just made a choice that excludes everything, and the old
   * screen's failure was that it could not say which. Keeping the previous
   * number lets the screen say "1,240 → 0" and offer the undo.
   *
   * Billable rather than contacts: with no contact role chosen the contact
   * count is legitimately zero while tens of thousands of companies match, and
   * reading that as "you have excluded everything" would fire the warning on
   * the one step where nothing is wrong.
   */
  lastNonEmpty: number | null;
}

export const countState = writable<CountState>({
  status: 'idle',
  value: null,
  lastNonEmpty: null
});

/**
 * How long to wait after a choice before asking for a count.
 *
 * The service rate-limits a client to one request a second with a burst of
 * three, and picking five cantons is five choices in about a second. Waiting
 * for the clicking to stop turns that into one request; without it the
 * customer's own selecting would rate-limit them out of their own count.
 */
const settleMs = 350;

let timer: ReturnType<typeof setTimeout> | undefined;

/**
 * Ask for a count once the customer has stopped choosing.
 *
 * Not on the similarity door. The count route answers "how many companies match
 * these filters", and a similarity search is not those filters: it starts from
 * a handful of examples and the filters only narrow what resemblance returned.
 * Counting the filters alone would answer a question nobody asked, in the tens
 * of thousands, next to a search that will return a few hundred.
 */
export function scheduleCount(): void {
  if (get(guidedDoor) === 'similarity') {
    clearTimeout(timer);
    countState.set({ status: 'idle', value: null, lastNonEmpty: null });
    return;
  }

  clearTimeout(timer);
  countState.update((s) => ({ ...s, status: 'counting' }));
  timer = setTimeout(runCount, settleMs);
}

async function runCount(): Promise<void> {
  try {
    const value = await fetchCount();

    // Null means a newer request overtook this one, or there are no criteria
    // yet. Neither is an answer to show.
    if (value === null) {
      countState.update((s) =>
        s.status === 'counting' ? { ...s, status: 'idle', value: null } : s
      );
      return;
    }

    const billable = billableRecords(value.companies, value.contacts);

    countState.update((s) => ({
      status: 'ready',
      value,
      lastNonEmpty: billable > 0 ? billable : s.lastNonEmpty
    }));
  } catch (error) {
    // A failed count must not stop somebody searching. The bar says nothing
    // and the search itself still works.
    logger.warn('guided: counting failed', error);
    countState.update((s) => ({ ...s, status: 'failed' }));
  }
}

/**
 * How many records the guided screen will ask the search to return.
 *
 * # Why there is a limit at all
 *
 * `/deep-iq/filter` applies no cap on the structured path: it returns a full
 * preview row for every company that matched. Measured against the live
 * corpus, that is 827 bytes a company — 17,559 companies of finance staff come
 * back as a 14.5 MB response in 1.7 seconds, which works. The same request for
 * the highest decision maker matches 702,388 companies, which is about 581 MB.
 * The app's own request timeout is 60 seconds and the browser has to hold the
 * result; neither survives that, and what the customer sees is a search that
 * produces nothing.
 *
 * The role-first door walks straight into it, because a role on its own is the
 * broadest thing this app can be asked for.
 *
 * # It matches the server's cap
 *
 * `maxPreviewCompanies` in internal/httpapi/selection.go is the same number,
 * and the server is the one that enforces it — a selection larger than this
 * comes back trimmed, with `stats.capped` set and `stats.company_count`
 * carrying what really matched.
 *
 * The guided screen stops first so that the customer is told to narrow while
 * they are still narrowing, rather than being handed a silently partial list
 * at the end. Since the count is exact, it can say by how much.
 *
 * Keep the two in step. If they drift apart, the screen either blocks searches
 * the server would have answered, or lets through ones it will trim.
 *
 * A third holds the same number: `maxQuotableRecords` in pricing, which is the
 * largest selection the summary bar puts a figure on. Above this nothing is
 * delivered, so above this there is nothing to price. A test asserts the two
 * are equal.
 */
export const resultCap = 30000;

/** Forget everything, for when the guided screen is opened afresh. */
export function resetGuided(): void {
  clearTimeout(timer);
  guidedDoor.set(null);
  guidedStep.set('door');
  countState.set({ status: 'idle', value: null, lastNonEmpty: null });
}

/**
 * Take a door: start the guide at that door's first step.
 *
 * Here rather than in a component because two now open doors — the member
 * home's tiles at the foot of the screen, and the sidebar beside it — and
 * "which step follows this door" is a fact about the guide, not about either
 * of them. When it lived in the home screen the sidebar would have had to
 * copy three lines and stay right about them.
 */
export function openDoor(door: Door): void {
  guidedDoor.set(door);
  guidedStep.set(stepsFor(door)[0]);
  guidedActive.set(true);
}

/**
 * Go back to the front screen.
 *
 * The door step with no door taken, which is what the members' app draws its
 * home from. `resetGuided` is deliberately not used: this is navigation, and
 * throwing away a count the customer is part-way through collecting is not
 * what clicking "Dashboard" asks for.
 */
export function returnToDoor(): void {
  guidedDoor.set(null);
  guidedStep.set('door');
  guidedActive.set(true);
}

/**
 * The steps after the door, in the order this customer is asked them.
 *
 * The door decides the order and nothing else: both halves are always
 * reachable and either can be skipped, so "HVAC, and the technical lead" is a
 * straight line rather than a branch somebody has to back out of.
 */
export function stepsFor(door: Door): StepName[] {
  // The two the door orders, then the ones every search shares. Everything
  // after `region` is optional and says so; they are ordered by how often a
  // customer has an opinion about them.
  // `exclude` is last on purpose: every step before it describes who the
  // customer is looking for, and it is the only one that takes companies away.
  // Asked earlier it would be asking somebody to subtract from nothing.
  //
  // `legalForm` sits next to `refine` because both describe the company rather
  // than how to reach it, and a customer who has just set a headcount floor is
  // in the frame of mind to say "and no Einzelfirmen" — which is the commonest
  // thing anybody asks of it.
  //
  // `technology` sits after `channels` and before `founding`, which is the
  // order of how often somebody has an opinion. It is a narrow filter with a
  // sharp audience — the customer selling IT services knows exactly what they
  // want from it — and it is still asked less often than how to reach a
  // company, and more often than the year one was founded.
  const rest: StepName[] = [
    'region',
    'refine',
    'legalForm',
    'channels',
    'technology',
    'founding',
    'exclude'
  ];

  // The similarity door asks for examples instead of an industry, and then the
  // same questions as the others. There is no industry step in it: the example
  // companies are the industry, and a centroid filter on top of them would
  // narrow a set that was chosen by resemblance using the taxonomy the customer
  // deliberately did not use.
  if (door === 'similarity') return ['samples', 'role', ...rest];

  // The list door reads the customer's own book and then shows them what it
  // said, so its first two steps are the file and the findings.
  //
  // `exclude` is absent, and its absence is the point: the file that was
  // profiled is the file to subtract, and it is applied when the findings are
  // accepted. Asking again at the end would be asking for the same upload
  // twice.
  //
  // `industry` and `region` stay in the sequence even though the profile has
  // usually filled them. They are the same components writing the same stores,
  // so a customer arriving at them finds an answer already there and can change
  // it — which is the point of showing the findings rather than acting on them.
  if (door === 'list') {
    return [
      'listUpload',
      'listProfile',
      'industry',
      'role',
      'region',
      'refine',
      'legalForm',
      'channels',
      'technology',
      'founding'
    ];
  }

  return door === 'industry' ? ['industry', 'role', ...rest] : ['role', 'industry', ...rest];
}

/**
 * How many of a door's leading steps are the door's own question.
 *
 * Everything after them is an optional filter, which the guide asks about
 * before it shows — see GuidedSearch. For three of the doors that is the single
 * step the door named.
 *
 * The list door has two, because its question takes two screens: the file, and
 * what the file turned out to say. Counting only the upload would put "do you
 * want to narrow by this?" above the findings, which asks the customer to
 * opt in to the answer they came for.
 */
export function mandatorySteps(door: Door): number {
  return door === 'list' ? 2 : 1;
}

/** Move to the next step, or return false when there is none. */
export function advance(): boolean {
  const door = get(guidedDoor);
  if (!door) return false;

  const steps = stepsFor(door);
  const at = steps.indexOf(get(guidedStep));
  if (at < 0 || at === steps.length - 1) return false;

  guidedStep.set(steps[at + 1]);
  return true;
}

/** Move back a step, or to the door. */
export function retreat(): void {
  const door = get(guidedDoor);
  if (!door) return;

  const steps = stepsFor(door);
  const at = steps.indexOf(get(guidedStep));

  if (at <= 0) {
    guidedDoor.set(null);
    guidedStep.set('door');
    return;
  }
  guidedStep.set(steps[at - 1]);
}
